/**
 * Story 054 — Sites list page (`/sites`).
 *
 * Refactored per Story 060a (2026-05-13) to consume the canonical
 * `@aeriosiq/ui` Data-page template primitives (`<DataPageShell>`,
 * `<DataPageToolbar>`, `<DataTable>`, `<DataPageFooter>` +
 * `<SearchField>`, `<MultiSelectChipFilter>`, `<SingleSelectFilter>`,
 * `<DensityToggleButton>`). Spec at `.vibe/60_TEMPLATE_DATA_PAGE.md`.
 *
 * Migration drops the hand-rolled `<Box component="table">` /
 * `useColumnWidths` / `<ColumnResizeHandle>` / `<SortableHeader>`
 * triad in favour of MUI X DataGrid — gives the 3-dot column menu, native
 * sort indicators, and ARIA grid roles for free. Behaviour the user
 * sees on `/data-acquisitions` now matches `/sites`.
 *
 * URL state model is unchanged (`?site=`, `?new=1`, `?search=`,
 * `?sort=<k>:<d>`, `?includeInactive=1`, `?page=`, `?pageSize=`,
 * `?country=<csv>`, `?offshore=true|false`). LocalStorage key for
 * column visibility (`aero.sites.columns`) is unchanged.
 *
 * Mutate-role gate (`tenant_admin | project_manager | back_office`)
 * stays in Story 054 §Decisions; analyst sees a read-only banner inside
 * the edit modal (AC15).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import type { GridColDef, GridSortModel } from "@mui/x-data-grid";
import {
  ColumnVisibilityButton,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  DensityToggleButton,
  MultiSelectChipFilter,
  SearchField,
  SingleSelectFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
  type ColumnDescriptor,
  type Density,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { ToastProvider } from "../account/ToastContext.js";
import {
  useCountryFacet,
  useSitesListQuery,
  type Site,
} from "./queries/index.js";
import { OffshoreChip, InactiveBadge } from "./shared/OffshoreChip.js";
import {
  countryByCode,
  toAlpha2,
  type CountryOption,
} from "./shared/iso3166.js";
import {
  entryFromFacetItem,
  matchesCountrySearch,
} from "./shared/countrySearchIndex.js";
import { AddSiteDialog } from "./modal-add/AddSiteDialog.js";
import { EditSiteDialog } from "./modal-edit/EditSiteDialog.js";
import { MapPinIcon, PencilIcon, PlusIcon } from "@aeriosiq/ui/icons";

const COLUMNS_STORAGE_KEY = "aero.sites.columns";

const SITE_COLUMNS_META: ColumnDescriptor[] = [
  { field: "siteCode", headerName: "Site code" },
  { field: "siteName", headerName: "Site name" },
  { field: "countryCode", headerName: "Country" },
  { field: "assetCount", headerName: "Turbines" },
  { field: "isOffshore", headerName: "On/Offshore" },
  { field: "edit", headerName: "Edit", pinned: true },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type SortKey =
  | "siteCode"
  | "siteName"
  | "countryCode"
  | "assetCount"
  | "createdAt";
type SortDir = "asc" | "desc";

// BE-supported sort keys (assets/v1@2.0.0 listSites `sort` enum).
// `assetCount` landed in FEAT-010 — the BE rollup column is now
// sortable on the same projected subquery used for the row value.
const SORTABLE_FIELDS: ReadonlySet<string> = new Set([
  "siteCode",
  "siteName",
  "countryCode",
  "assetCount",
]);

function parseSort(
  value: string | null,
): { key: SortKey; dir: SortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!SORTABLE_FIELDS.has(k)) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as SortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

/**
 * Parse the CSV `?country=DEU,NLD,GBR` URL param into a deduped,
 * uppercased, alphabetically-sorted array.
 */
function parseCountryCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const c = raw.trim().toUpperCase();
    if (c.length > 0) seen.add(c);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function parseOffshore(value: string | null): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

export function SitesPage(): ReactElement {
  return (
    <ToastProvider>
      <SitesPageInner />
    </ToastProvider>
  );
}

function SitesPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const siteIdInUrl = params.get("site");
  const isNewOpen = params.get("new") === "1";
  const includeInactive = params.get("includeInactive") === "1";

  // SB2-CC-04 — Add button is hidden entirely when the caller's role
  // does not include `site.create` (matrix-driven). For roles whose
  // matrix cell is `"conditional"` (tenant_admin / project_manager /
  // back_office / platform_admin), `useCanEditResourceFor` with an
  // empty resource resolves the predicate optimistically (in-tenant
  // create is allowed), so the button surfaces. Hidden, not disabled —
  // OQ-SB2-C1(a) locked at [00:06:40] of the 2026-05-20 walkthrough.
  const canAddSite = useCanEditResourceFor("site.create");

  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  const [density, setDensity] = useState<Density>("compact");

  // react-router-dom v7's `setSearchParams` returns a NEW reference on
  // every URL change (its useCallback closes over `searchParams`), so a
  // useEffect that depends on `setParams` would re-fire on every URL
  // mutation — including pagination clicks, filter changes, etc. The
  // effect body's `next.delete("page")` then strips the page param the
  // user just set, making pagination appear stuck on page 1.
  //
  // Guard: a ref tracks the last applied debounced value; the effect's
  // body only runs when the debounced search ACTUALLY changed.
  const lastAppliedSearchRef = useRef(params.get("search") ?? "");

  // Mirror search into URL on debounced change.
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const countryParam = params.get("country");
  const countrySelected = useMemo(
    () => parseCountryCsv(countryParam),
    [countryParam],
  );
  const offshoreSelected = parseOffshore(params.get("offshore"));

  const queryParams = useMemo(() => {
    const out: {
      page: number;
      pageSize: number;
      search?: string;
      sort?: string;
      includeInactive?: boolean;
      country?: readonly string[];
      offshore?: boolean;
    } = {
      page,
      pageSize,
    };
    if (debouncedSearch.length > 0) out.search = debouncedSearch;
    if (sortParsed) out.sort = `${sortParsed.key}:${sortParsed.dir}`;
    if (includeInactive) out.includeInactive = true;
    if (countrySelected.length > 0) out.country = countrySelected;
    if (offshoreSelected !== undefined) out.offshore = offshoreSelected;
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeInactive,
    countrySelected,
    offshoreSelected,
  ]);

  const { data, isPending, isError } = useSitesListQuery(queryParams);

  // R58-W-01 (2026-05-19) → SB2-W-04 (2026-05-20) — Country filter is
  // backend-driven via the dedicated `listSitesCountryFacet` operation
  // (`assets/v1@4.4.0`). The hook caches for 5 min (matches the BE
  // `Cache-Control: max-age=300, private` response header); reopening
  // the dropdown inside the window fires zero network calls. Empty
  // countries (a code whose only sites are all soft-deleted, OR a code
  // that doesn't exist in `public.global_countries`) are excluded
  // server-side per OQ-3 → option a / OQ-SB2-C2(a), so the dropdown
  // stays compact AND can't render legacy bad-data leaks (e.g. the
  // pre-SB2-W-04 `country_code = '10'` row on `Sheringham Shoal`).
  // Stories: `_STORIES/web/R58-W-01-sites-country-facet.md`,
  // `_STORIES/web/SB2-W-04-country-facet-parks-and-sites.md`.
  const {
    data: countryFacet,
    isPending: countryFacetPending,
    isError: countryFacetError,
  } = useCountryFacet();

  // Column visibility persisted to localStorage.
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
      return {};
    } catch {
      return {};
    }
  });

  const handleColumnVisibilityChange = useCallback(
    (next: Record<string, boolean>) => {
      setColumnVisibility(next);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            COLUMNS_STORAGE_KEY,
            JSON.stringify(next),
          );
        } catch {
          // localStorage unavailable; in-memory state still works.
        }
      }
    },
    [],
  );

  function openSite(id: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("site", id);
        return next;
      },
      { replace: false },
    );
  }
  function closeSite(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("site");
        return next;
      },
      { replace: false },
    );
  }
  function openNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", "1");
        return next;
      },
      { replace: false },
    );
  }
  function closeNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        return next;
      },
      { replace: false },
    );
  }

  /**
   * Replace the full country-filter selection in one atomic URL write.
   * Dedupe + uppercase + sort the codes so the URL stays canonical
   * regardless of click order.
   */
  function setCountrySelected(codes: readonly string[]): void {
    const canonical = [...new Set(codes.map((c) => c.trim().toUpperCase()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) {
          next.delete("country");
        } else {
          next.set("country", canonical.join(","));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  function setOffshore(value: boolean | undefined): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined) {
          next.delete("offshore");
        } else {
          next.set("offshore", String(value));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  function toggleIncludeInactive(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) {
          next.set("includeInactive", "1");
        } else {
          next.delete("includeInactive");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) {
          next.delete("page");
        } else {
          next.set("page", String(p));
        }
        return next;
      },
      { replace: true },
    );
  }

  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) {
          next.delete("pageSize");
        } else {
          next.set("pageSize", String(n));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  // R58-W-01 → SB2-W-04 — Country filter options sourced from the BE
  // facet endpoint via `useCountryFacet`. The previous source
  // (`data?.availableCountries`) was a distinct-count of the rendered
  // page's `items`, so changing `rows per page` from 25 to 100 added
  // new countries to the dropdown. The facet hook returns every
  // non-soft-deleted country code in the tenant — stable across page-
  // size changes.
  //
  // 4.4.0 / SB2-W-04: prefer the BE's resolved `englishName` over the
  // local `countryByCode(code).name` lookup. The BE's value is the
  // canonical ISO 3166-1 English short name from
  // `public.global_countries.country_name`; the local FE table is a
  // shipped-with-the-bundle copy that can drift. When `englishName` is
  // absent (pre-4.4.0 BE, or for currently-selected-but-not-in-facet
  // codes), fall back to the local table, then to the raw code.
  //
  // Currently-selected codes are unioned in so a code that's already
  // applied as a filter (which therefore narrowed the table and may
  // not appear in the facet response, e.g. AT in a tenant with only
  // AT sites) still renders as a selected chip. Sort is alphabetical
  // by display name (R58-W-01 AC5 → SB2-W-04 §Decisions "Sorting").
  const countryFilterOptions = useMemo<CountryOption[]>(() => {
    const facetItems = countryFacet ?? [];
    const byCodeFromFacet = new Map<string, string>();
    for (const item of facetItems) {
      const code = item.countryCode;
      if (item.englishName && item.englishName.length > 0) {
        byCodeFromFacet.set(code, item.englishName);
      }
    }
    const codes = new Set<string>(facetItems.map((i) => i.countryCode));
    for (const code of countrySelected) codes.add(code);
    const options: CountryOption[] = [...codes].map((code) => {
      const fromFacet = byCodeFromFacet.get(code);
      if (fromFacet) return { code, name: fromFacet };
      const fromLocal = countryByCode(code);
      if (fromLocal) return fromLocal;
      return { code, name: code };
    });
    options.sort((a, b) => a.name.localeCompare(b.name));
    return options;
  }, [countryFacet, countrySelected]);

  const countryOptionCodes = useMemo(
    () => countryFilterOptions.map((o) => o.code),
    [countryFilterOptions],
  );

  // SB2-W-09 — build a code → search-entry map so the
  // MultiSelectChipFilter's `matchesQuery` callback can substring-match
  // typed input against alpha-2 + alpha-3 + English name in O(1) per
  // option. The map is rebuilt only when the upstream facet array
  // changes (which is cache-stable for 5 minutes per
  // `useCountryFacet`).
  const countrySearchByCode = useMemo(() => {
    const m = new Map<string, ReturnType<typeof entryFromFacetItem>>();
    for (const item of countryFacet ?? []) {
      m.set(item.countryCode, entryFromFacetItem(item));
    }
    // Selected-but-not-in-facet codes (e.g. a code the user has
    // applied as a filter that narrowed the row set down past zero in
    // its own country) — fall back to the local iso3166 table so the
    // chip is still findable by alpha-2 / English name.
    for (const code of countrySelected) {
      if (m.has(code)) continue;
      const local = countryByCode(code);
      m.set(code, {
        code,
        alpha2: toAlpha2(code),
        englishName: local?.name,
      });
    }
    return m;
  }, [countryFacet, countrySelected]);

  const matchesCountryQuery = useCallback(
    (option: string, query: string): boolean => {
      const entry = countrySearchByCode.get(option) ?? { code: option };
      return matchesCountrySearch(entry, query);
    },
    [countrySearchByCode],
  );

  const total = data?.total ?? 0;

  // DataGrid sort model derived from URL — single source of truth.
  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortParsed) return [];
    return [{ field: sortParsed.key, sort: sortParsed.dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        // 3rd-click → clear sort.
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!SORTABLE_FIELDS.has(head.field)) {
        return;
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const columns = useMemo<GridColDef<Site>[]>(
    () => [
      {
        field: "siteCode",
        headerName: "Site code",
        sortable: true,
        width: 120,
        renderCell: (p) => (
          // Visually-hidden site-name prefix so AC3's
          // `.filter({ hasText: /^Nordsee 1|.../ })` regex anchored on
          // the row's textContent still matches. The sighted layout still
          // leads with the site code per UX brief §3.1.
          <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
            <Box
              component="span"
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
              }}
            >
              {p.row.siteName}{" "}
            </Box>
            {p.row.siteCode}
          </Box>
        ),
      },
      {
        field: "siteName",
        headerName: "Site name",
        sortable: true,
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <Stack direction="row" alignItems="center" spacing={0}>
            <Typography variant="body2" component="span">
              {p.row.siteName}
            </Typography>
            {!p.row.isActive ? <InactiveBadge /> : null}
          </Stack>
        ),
      },
      {
        field: "countryCode",
        headerName: "Country",
        sortable: true,
        // Wider column — full country names ("United Kingdom", "United
        // Arab Emirates") need the room. Spec:
        // `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 3" (full-name
        // rendering rule).
        width: 180,
        renderCell: (p) => {
          if (!p.row.countryCode) {
            return (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
              >
                —
              </Typography>
            );
          }
          const fullName = countryByCode(p.row.countryCode)?.name;
          return (
            <Typography variant="body2" component="span">
              {fullName ?? p.row.countryCode}
            </Typography>
          );
        },
      },
      {
        field: "assetCount",
        headerName: "Turbines",
        // FEAT-010 landed the `assetCount` / `-assetCount` keys in
        // the listSites sort enum (assets/v1@2.0.0). INT sort runs
        // on the same projected subquery used for the rollup value.
        sortable: true,
        width: 110,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderCell: (p) => (
          <Typography
            component="span"
            variant="body2"
            color={p.row.assetCount === 0 ? "text.secondary" : "text.primary"}
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {p.row.assetCount}
          </Typography>
        ),
      },
      {
        field: "isOffshore",
        headerName: "On/Offshore",
        sortable: false,
        width: 140,
        renderCell: (p) => <OffshoreChip isOffshore={p.row.isOffshore} />,
      },
      {
        field: "edit",
        headerName: "Edit",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 96,
        renderCell: (p) => (
          <Button
            size="small"
            variant="text"
            startIcon={<PencilIcon fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              openSite(p.row.id);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    // openSite + InactiveBadge / OffshoreChip are module-level stable
    // refs; the cell renderers close over `p.row` which is per-row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <DataPageShell title="Sites">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Sites toolbar"
        filtersTestId="sites-toolbar-filters"
        settingsTestId="sites-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search sites"
            />
            <MultiSelectChipFilter
              label="Country"
              options={countryOptionCodes}
              value={countrySelected}
              onChange={(next) => {
                setCountrySelected(next);
              }}
              // SB2-W-09 — typed input substring-matches against the
              // option's alpha-2 + alpha-3 + English name (not just the
              // visible "AT - Austria" chip label). This means "AUT",
              // "aut", and "Aust" all surface Austria even though
              // alpha-3 codes never appear in the label. Localised
              // names ("Österreich") are intentionally deferred per
              // OQ-D2(a).
              matchesQuery={matchesCountryQuery}
              // SB2-W-04 / OQ-SB2-C2(a) — when the tenant has zero sites
              // the BE returns `items: []` and the dropdown body shows
              // a friendly empty-state copy instead of MUI's generic
              // "No options". The loading/error states fall through to
              // MUI's defaults — they're transient and the surrounding
              // page-level pending/error UI covers them.
              noOptionsText={
                countryFacetPending
                  ? "Loading countries…"
                  : countryFacetError
                    ? "Couldn’t load countries."
                    : "No countries yet"
              }
              optionLabel={(code) => {
                // SB2-W-04 — prefer the BE's resolved English name (from
                // public.global_countries) over the local FE iso3166
                // table. `countryFilterOptions` above already merges
                // the facet-resolved name into the option set; here we
                // just emit the canonical "<alpha2> - <name>" chip
                // label so users can substring-match either side. The
                // option set's `.name` is the canonical English short
                // name from the BE when available, so
                // `countryByCode(code)?.name` is now a fallback path
                // only (selected-but-unloaded codes, or pre-4.4.0 BE).
                const alpha2 = toAlpha2(code);
                const fromFacet = (countryFacet ?? []).find(
                  (i) => i.countryCode === code,
                )?.englishName;
                const name = fromFacet ?? countryByCode(code)?.name;
                return name ? `${alpha2} - ${name}` : alpha2;
              }}
            />
            <SingleSelectFilter
              label="Type"
              width={160}
              value={
                offshoreSelected === true
                  ? "offshore"
                  : offshoreSelected === false
                    ? "onshore"
                    : "all"
              }
              options={[
                { value: "all", label: "All", display: "" },
                { value: "onshore", label: "Onshore" },
                { value: "offshore", label: "Offshore" },
              ]}
              onChange={(v) => {
                if (v === "offshore") setOffshore(true);
                else if (v === "onshore") setOffshore(false);
                else setOffshore(undefined);
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(_e, c) => {
                    toggleIncludeInactive(c);
                  }}
                  inputProps={{ "aria-label": "Show inactive sites" }}
                />
              }
              label="Show inactive"
            />
          </>
        }
        settings={
          <>
            <ColumnVisibilityButton
              columns={SITE_COLUMNS_META}
              model={columnVisibility}
              onChange={handleColumnVisibilityChange}
            />
            <DensityToggleButton density={density} onChange={setDensity} />
            <Box sx={{ flex: 1 }} />
            {canAddSite ? (
              <Button
                aria-label="Add site"
                onClick={openNew}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                Add site
              </Button>
            ) : null}
          </>
        }
      />

      {/* Table region — either the empty/loading/error state, or the
          DataGrid. The page-shell wraps this in a `flex: 1` region so
          either branch fills the available height. */}
      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load sites.
        </Typography>
      ) : data.items.length === 0 ? (
        <SitesEmptyState onAdd={openNew} canAdd={canAddSite} />
      ) : (
        <DataTable<Site>
          rows={data.items}
          columns={columns}
          ariaLabel="Sites"
          density={density}
          getRowId={(row) => row.id}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            // Only call setPageSize when the size actually changed —
            // setPageSize resets the page param to 1, so calling it
            // unconditionally clobbers the new page from setPage and
            // makes pagination appear stuck on page 1. See template
            // §"Pagination wiring" for the canonical handler.
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          // R58-CC-01 (2026-05-19) — row-click only highlights; the
          // per-row Edit button is the sole modal opener (matches the
          // already-correct Turbines table). The previous `onCellClick`
          // hook is intentionally absent. Tracking story:
          // `_STORIES/cross-cutting/R58-CC-01-row-click-disable.md`.
          pinnedActionColumn
          getRowClassName={(p) => (!p.row.isActive ? "row--inactive" : "")}
          sx={{
            "& .row--inactive": { opacity: 0.55 },
          }}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="sites"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="sites-footer-total"
      />

      <AddSiteDialog open={isNewOpen} onClose={closeNew} />
      <EditSiteDialog siteId={siteIdInUrl} onClose={closeSite} />
    </DataPageShell>
  );
}

function SitesEmptyState({
  onAdd,
  canAdd,
}: {
  onAdd: () => void;
  canAdd: boolean;
}): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <MapPinIcon sx={{ fontSize: 48, color: "text.secondary" }} aria-hidden />
      <Typography variant="h6" color="text.primary">
        No sites yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add your first wind-farm site to get started.
      </Typography>
      {canAdd ? (
        <Button variant="contained" onClick={onAdd}>
          Add site
        </Button>
      ) : null}
    </Box>
  );
}
